// client/src/telemetry/suiteTelemetry.ts
//
// Reports Navigator usage into the LGM Explorer suite's SHARED Application
// Insights component (appi-lgm-explorer) so Navigator appears in the
// Intelligence Hub's Suite Usage Analytics alongside the other suite apps.
// This is separate from — and additive to — the API's own server-side
// Application Insights (api/appInsights.js, Navigator's own component).
//
// Contract with the suite (webapp/public/lgm-telemetry.js in the Explorer
// repo). Deviating from any of these breaks cross-app segmentation or user
// dedup in the Hub dashboard:
//   * The connection string below is the suite's shared INGESTION key —
//     browser-safe by design; every suite page already ships it client-side.
//   * Every item must carry customDimensions.app = "LGM API Navigator"
//     (app names longer than 20 chars are clipped by the dashboard).
//   * user_AuthenticatedId must be the Entra DISPLAY NAME (not the UPN/email)
//     run through the suite's exact sanitize rule (lowercase, [,;=|\s]+ -> "_"),
//     because the AI SDK silently drops ids containing spaces/commas and the
//     suite keys all user metrics on the sanitized display name.
//   * Exactly one pageView per page load, named after the app, sent only
//     after the authenticated user context is set.
//
// Only production (www.lgmapinavigator.com) reports: stage, localhost, and
// Playwright E2E runs stay silent so they never inflate the executive usage
// numbers. Set localStorage lgm_suite_telemetry_force = "1" to test from a
// non-production host.

const SUITE_AI_CONN_STR =
  'InstrumentationKey=57b0e09b-7c7a-4b1f-a23f-32933489e860;' +
  'IngestionEndpoint=https://eastus-8.in.applicationinsights.azure.com/;' +
  'LiveEndpoint=https://eastus.livediagnostics.monitor.azure.com/;' +
  'ApplicationId=d01d0add-98eb-471e-801f-425025f8e39d';
const AI_SDK_URL = 'https://js.monitor.azure.com/scripts/b/ai.3.gbl.min.js';
const APP_NAME = 'LGM API Navigator'; // 17 chars — under the 20-char cap
const PROD_HOSTS = /^(www\.)?lgmapinavigator\.com$/i;

// Structural subset of MSAL's AccountInfo — keeps this module decoupled from
// whatever shape useAuth hands back.
type MsalAccountLike = {
  name?: string;
  username?: string;
  idTokenClaims?: Record<string, unknown>;
};

// Minimal shape of the CDN-loaded AI SDK (window.Microsoft.ApplicationInsights)
// and the telemetry item its initializer receives — just enough to type this
// module without depending on the SDK's own (unshipped) type package.
type SuiteTelemetryItem = {
  baseData?: { properties?: Record<string, unknown> };
  tags?: Record<string, unknown>;
};
type SuiteAppInsights = {
  loadAppInsights: () => SuiteAppInsights;
  addTelemetryInitializer: (init: (env: SuiteTelemetryItem) => void) => void;
  setAuthenticatedUserContext: (
    userId: string,
    accountId: string | undefined,
    storeInCookie: boolean
  ) => void;
  trackPageView: (item: { name: string }) => void;
};
type SuiteWindow = Window & {
  __lgmSuiteTelemetryStarted?: boolean;
  __E2E_TEST__?: boolean;
  Microsoft?: {
    ApplicationInsights?: {
      ApplicationInsights: new (config: {
        config: Record<string, unknown>;
      }) => SuiteAppInsights;
    };
  };
};

// Identical to the suite's sanitizeId (webapp/public/lgm-telemetry.js).
// Exported for unit testing only — not part of the module's public contract.
export function sanitizeId(s: unknown): string {
  return String(s == null ? '' : s)
    .toLowerCase()
    .trim()
    .replace(/[,;=|\s]+/g, '_');
}

export function displayNameOf(account?: MsalAccountLike | null): string {
  if (!account) return '';
  // Display name first — account.username is the UPN (george.goldsmith@...),
  // which would NOT dedup against the suite's display-name-keyed ids.
  const claimName = account.idTokenClaims && account.idTokenClaims.name;
  return (
    account.name ||
    (typeof claimName === 'string' ? claimName : '') ||
    account.username ||
    ''
  );
}

let started = false;

/**
 * Fire-once-per-page-load usage beacon. Call after the user is authenticated
 * and role-validated; safe to call repeatedly (idempotent) and safe to call
 * anywhere — it can never throw into the app.
 */
export function reportSuiteUsage(account?: MsalAccountLike | null): void {
  try {
    if (started || typeof window === 'undefined') return;
    const w = window as SuiteWindow;
    if (w.__lgmSuiteTelemetryStarted) return; // HMR / double-mount guard
    if (w.__E2E_TEST__ === true) return;

    let force = false;
    try {
      force = window.localStorage.getItem('lgm_suite_telemetry_force') === '1';
    } catch {
      // storage unavailable (private mode) — treat as no override
    }
    if (!PROD_HOSTS.test(window.location.hostname) && !force) return;

    started = true;
    w.__lgmSuiteTelemetryStarted = true;

    const userId = sanitizeId(displayNameOf(account));

    const boot = () => {
      try {
        const M = (window as SuiteWindow).Microsoft;
        if (!M || !M.ApplicationInsights) return;
        const ai = new M.ApplicationInsights.ApplicationInsights({
          config: {
            connectionString: SUITE_AI_CONN_STR,
            enableAutoRouteTracking: false,
            disableFetchTracking: true,
            disableAjaxTracking: true,
            autoTrackPageVisitTime: true,
            enableSessionStorageBuffer: true,
          },
        }).loadAppInsights();

        // Stamp every item with the app dimension (+ user, belt-and-suspenders)
        // exactly like the suite's telemetry initializer does.
        ai.addTelemetryInitializer((env: SuiteTelemetryItem) => {
          const b = (env.baseData = env.baseData || {});
          b.properties = b.properties || {};
          b.properties.app = APP_NAME;
          if (userId) {
            env.tags = env.tags || {};
            if (!env.tags['ai.user.authUserId']) {
              env.tags['ai.user.authUserId'] = userId;
            }
          }
        });

        if (userId) {
          try {
            ai.setAuthenticatedUserContext(userId, undefined, true);
          } catch {
            // non-fatal — the initializer above still tags items
          }
        }
        ai.trackPageView({ name: APP_NAME });
      } catch (e) {
        console.warn('[suite-telemetry] init failed:', e);
      }
    };

    if (w.Microsoft && w.Microsoft.ApplicationInsights) {
      boot();
      return;
    }
    const s = document.createElement('script');
    s.src = AI_SDK_URL;
    s.async = true;
    s.onload = boot;
    s.onerror = () => {
      console.warn('[suite-telemetry] AI SDK failed to load');
      started = false;
      w.__lgmSuiteTelemetryStarted = false;
    };
    document.head.appendChild(s);
  } catch (e) {
    console.warn('[suite-telemetry] disabled:', e);
  }
}
